import { iqCategoryMeta } from '@/mocks/iqTestData';

interface Props {
  onStart: () => void;
}

export default function IqIntro({ onStart }: Props) {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Hero Card */}
      <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-white text-center mb-6">
        <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
          <i className="ri-psychotherapy-line text-3xl text-white"></i>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-3">
          종합 인지능력(IQ) 검사
        </h1>
        <p className="text-primary-100 text-sm md:text-base leading-relaxed">
          도형 패턴, 숫자 수열, 도형 겹치기, 3D 입체 등 6개 영역을 평가하여
          <br className="hidden md:block" />
          나의 종합적인 인지능력 수준을 파악해 드립니다.
        </p>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { icon: 'ri-time-line', title: '제한 시간', value: '30분' },
          { icon: 'ri-question-answer-line', title: '문항 수', value: '총 100문항' },
          { icon: 'ri-pie-chart-line', title: '평가 영역', value: '6개 영역' },
        ].map((item) => (
          <div key={item.title} className="bg-background-50 border border-background-200 rounded-xl p-4 text-center">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-2">
              <i className={`${item.icon} text-primary-600`}></i>
            </div>
            <div className="font-bold text-foreground-950">{item.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{item.title}</div>
          </div>
        ))}
      </div>

      {/* 4 Categories */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-layout-grid-line text-accent-500"></i>
          6개 평가 영역
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {iqCategoryMeta.map((cat) => (
            <div key={cat.id} className="flex items-start gap-3 border border-background-200 rounded-lg p-3">
              <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                <i className={`${cat.icon} text-primary-600`}></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 text-sm">{cat.label}</div>
                <div className="text-foreground-600 text-xs leading-relaxed mt-0.5">{cat.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* IQ 기준 안내 */}
      <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <i className="ri-information-line text-accent-500 flex-shrink-0 mt-0.5"></i>
          <div className="text-accent-700 text-sm leading-relaxed">
            <strong>평균 IQ 기준:</strong> 웩슬러 지능검사에서는 평균을 <strong>100</strong>으로 봅니다.
            <strong>90~109</strong>는 평균 범위, <strong>110 이상</strong>은 평균보다 높은 수준,
            <strong>120 이상</strong>은 우수, <strong>130 이상</strong>은 매우 높은 수준으로 분류합니다.
          </div>
        </div>
      </div>

      {/* Notice */}
      <div className="bg-background-100 border border-background-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <i className="ri-shield-check-line text-primary-500 flex-shrink-0 mt-0.5"></i>
          <p className="text-foreground-600 text-sm leading-relaxed">
            이 검사는 재미와 자기 이해를 위한 <strong>참고용 온라인 검사</strong>입니다.
            임상 심리학자의 정식 지능검사(웩슬러 검사 등)를 대체할 수 없으며,
            결과는 대략적인 추정치로 이해해 주세요.
          </p>
        </div>
      </div>

      <button
        onClick={onStart}
        className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-4 rounded-xl text-base transition-colors cursor-pointer"
      >
        검사 시작하기 <i className="ri-arrow-right-line ml-2"></i>
      </button>
    </div>
  );
}